import React from "react";
import toast from "react-hot-toast";
import useOpenMasterAccount from "../../../hooks/useOpenMasterAccount";
import ReportsTableStatusColumm from "../../../components/tables/columns/ReportsTableStatusColumn";
import ReportManagementDetailTableRowEfficacyColumn from "./ReportManagementDetailTableRowEfficacyColumn";
import ReportManagementDetailTableRowColumn from "./ReportManagementDetailTableRowColumn";
import ReportsTableActionColumn from "../../../components/tables/columns/ReportsTableActionColumn";
import ReportDateDisplayColumn, { ReportDeadlineDateDisplayColumn } from "../../../components/tables/ReportDateDisplayColumn";
import { useReportPdfData } from "../../../hooks/useReportPdfData";
import { useClientReportsList } from "../../../hooks/ClientReportContext";
import { DateDisplay, ProfileDisplay } from "../../../components";
import { ThemedSpan } from "../../../components/utilities";
import { TableLoadingRow } from "../../../components/tables/VirtualTable";
import { openPdfViewerModal } from "../../../components/modals/ViewPdfModal";
import { ClientReportType } from "../../../types/ClientReportType";

export type ReportManagementDetailTableRowProps = {
    data: ClientReportType[],
    index: number,
    style?: any
};

const ReportManagementDetailTableRow = (
    { data, index, style } : Readonly<ReportManagementDetailTableRowProps>
) => {
    const { height } = style;
    const { getStatusDescription, openReport } = useClientReportsList()!;
    const { getPdfBytes } = useReportPdfData(data[index]?.masterAccountId, data[index]?.id);
    const openMasterAccount = useOpenMasterAccount();

    // do not return a data row when the data has not yet been collected from the backend
    if (index >= data.length) {
        return <TableLoadingRow style={{ height }} />
    }

    const {
        adviserId,
        documentId,
        documentPdfId,
        typeDescription, 
        reportName,
        masterAccountId,
        masterAccountName,
        projectId,
        status,
        authorId,
        efficacy,
        deadlineDate,
        compliantDate,
        sentDate,
        createdDate,
        isPdfGenerated,
        isReviewReport,
        isStatusTerminated
    } = data[index];

    const handleOpenDocument = (e) => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }
        if (isStatusTerminated === false && openReport && typeof(openReport) === 'function') {
            openReport(documentId, reportName);
            return;
        }
        if (isStatusTerminated === true && isPdfGenerated === true && documentPdfId) {
            getPdfBytes().then(
                r => openPdfViewerModal({ data: r, title: data[index].reportName })
            );
            return;
        }
        toast.error('Could not open report as unable to find the document to open.');
    };

    const handleOpenMasterAccount = (e) => {
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }

        let suffix = isReviewReport
            ? 'reviews'
            : projectId 
                ? `projects?projectId=${projectId}` 
                : `projects`;

        openMasterAccount(masterAccountId, { suffix });
    };

    return (
        <tr style={{ height }}>
            {/* Display the Adviser Profile Details */}
            <ReportManagementDetailTableRowColumn width={12.5} style={{ height }}>
                {adviserId ? (<ProfileDisplay userId={adviserId} />) : (<span>-</span>)}
            </ReportManagementDetailTableRowColumn>
            {/* The Report Name & Type combined into two rows */}
            <ReportManagementDetailTableRowColumn width={15}>
                <p className="text-truncate m-0 lh-1">
                    <a href="#" onClick={handleOpenDocument}>{reportName}</a>
                    <br />
                    <ThemedSpan variant="muted" className="fs-smallest">{typeDescription}</ThemedSpan>
                </p>
            </ReportManagementDetailTableRowColumn>
            {/* Client Details */}
            <ReportManagementDetailTableRowColumn width={15}>
                <a href={isReviewReport ? `client/${masterAccountId}/reviews` : `/client/${masterAccountId}/projects${projectId ? `?projectId=${projectId}` : ''}`} onClick={handleOpenMasterAccount}>
                    <span>{masterAccountName}</span>
                </a>
            </ReportManagementDetailTableRowColumn>
            {/* Created Date */}
            <ReportManagementDetailTableRowColumn width={7.5}>
                <DateDisplay>{createdDate}</DateDisplay>
            </ReportManagementDetailTableRowColumn>
            {/* Status */}
            <ReportManagementDetailTableRowColumn width={7.5}>
                <ReportsTableStatusColumm data={data[index]} getStatusDescription={getStatusDescription} />
            </ReportManagementDetailTableRowColumn>
            {/* Author Profile Data */}
            <ReportManagementDetailTableRowColumn width={12.5}>
                {authorId ? (<ProfileDisplay userId={authorId} />) : (<span>-</span>)}
            </ReportManagementDetailTableRowColumn>
            {/* Deadline Display */}
            <ReportDeadlineDateDisplayColumn width={7.5} deadlineDate={deadlineDate} status={status} />
            {/* Efficacy Icon */}
            <ReportManagementDetailTableRowEfficacyColumn width={5} efficacy={efficacy} />
            {/* Compliant Date */}
            <ReportDateDisplayColumn width={7.5} date={compliantDate} deadlineDate={deadlineDate} />
            {/* Sent Date */}
            <ReportDateDisplayColumn width={7.5} date={sentDate} deadlineDate={deadlineDate} />
            {/* Action Column */}
            <ReportsTableActionColumn data={data[index]} width={2.5} />
        </tr>
    );
};

export default ReportManagementDetailTableRow;